import { checkExactMathAnswer, exactAnswerPreview } from "../../../../utils/exactMathAnswer";
import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const TYPES = [
  "FractionProductBeforeSumTask",
  "FractionQuotientBeforeDifferenceTask",
  "FractionParenthesesThenProductTask",
  "MixedNumberWithProductTask",
  "MixedNumberParenthesesDivisionTask",
  "MixedNumberFourOperationsTask",
  "FractionParenthesesSquareTask",
  "FractionDecimalMixedOperationsTask",
];

// Keep the displayed fractions small; also bound the work after multiplication/division.
export const MAX_WORKING_DENOMINATOR = 32;

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function fraction(num, den = 1) {
  if (!Number.isInteger(num) || !Number.isInteger(den) || den === 0) throw new Error("Invalid fraction");
  const divisor = gcd(num, den);
  const sign = den < 0 ? -1 : 1;
  return { num: sign * num / divisor, den: Math.abs(den) / divisor };
}

const value = (number, mixed = false) => ({ kind: "value", number, mixed });
const operation = (op, left, right) => ({ kind: "operation", op, left, right });
const group = (inner) => ({ kind: "group", inner });
const choose = (items) => items[Math.floor(Math.random() * items.length)];

function proper(used = new Set(), signed = false) {
  const den = choose([2, 3, 4, 5, 6, 7, 8, 9, 10, 12].filter((item) => !used.has(item)));
  used.add(den);
  const nums = Array.from({ length: den - 1 }, (_, index) => index + 1)
    .filter((num) => gcd(num, den) === 1);
  return value(fraction((signed && Math.random() < 0.35 ? -1 : 1) * choose(nums), den));
}

function mixed(used = new Set()) {
  const part = proper(used).number;
  const whole = choose([1, 2, 3]);
  return value(fraction(whole * part.den + part.num, part.den), true);
}

function makeExpression(type) {
  const used = new Set();
  const p = () => proper(used);
  const factor = () => proper(used, true);
  const m = () => mixed(used);
  if (type === TYPES[0]) return operation("+", p(), operation("*", factor(), factor()));
  if (type === TYPES[1]) return operation("-", p(), operation("/", factor(), factor()));
  if (type === TYPES[2]) return operation("*", group(operation(choose(["+", "-"]), p(), p())), factor());
  if (type === TYPES[3]) return operation(choose(["+", "-"]), m(), operation("*", factor(), factor()));
  if (type === TYPES[4]) return operation("/", group(operation(choose(["+", "-"]), m(), p())), factor());
  if (type === TYPES[6]) return operation(choose(["+", "-"]), { kind: "square", inner: group(operation(choose(["+", "-"]), p(), p())) }, p());
  if (type === TYPES[7]) {
    const numerator = choose([1, 3, 7, 9, 11, 13]);
    const decimal = { ...value(fraction(numerator, 10)), decimal: String(numerator / 10).replace(".", ",") };
    used.add(decimal.number.den);
    return operation("/", group(operation(choose(["+", "-"]), decimal, p())), factor());
  }
  return operation("-", m(), operation("/", operation("*", group(operation("+", p(), p())), factor()), factor()));
}

export function evaluate(node) {
  if (node.kind === "value") return node.number;
  if (node.kind === "group") return evaluate(node.inner);
  if (node.kind === "square") {
    const inner = evaluate(node.inner);
    return fraction(inner.num ** 2, inner.den ** 2);
  }
  const left = evaluate(node.left);
  const right = evaluate(node.right);
  if (node.op === "+") return fraction(left.num * right.den + right.num * left.den, left.den * right.den);
  if (node.op === "-") return fraction(left.num * right.den - right.num * left.den, left.den * right.den);
  if (node.op === "*") return fraction(left.num * right.num, left.den * right.den);
  if (right.num === 0) throw new Error("Division by zero");
  return fraction(left.num * right.den, left.den * right.num);
}

function fractionLatex(number) {
  if (number.den === 1) return `${number.num}`;
  const sign = number.num < 0 ? "-" : "";
  return `${sign}\\frac{${Math.abs(number.num)}}{${number.den}}`;
}

function mixedLatex(number) {
  const whole = Math.floor(Math.abs(number.num) / number.den);
  const rest = Math.abs(number.num) % number.den;
  if (!whole || !rest) return fractionLatex(number);
  const sign = number.num < 0 ? "-" : "";
  return `${sign}${whole}\\frac{${rest}}{${number.den}}`;
}

const symbol = { "+": "+", "-": "-", "*": "\\cdot", "/": ":" };

function expressionLatex(node) {
  if (node.kind === "value") {
    const latex = node.decimal ? node.decimal.replace(",", "{,}") : node.mixed ? mixedLatex(node.number) : fractionLatex(node.number);
    return node.number.num < 0 ? "\\left(" + latex + "\\right)" : latex;
  }
  if (node.kind === "square") return expressionLatex(node.inner) + "^{2}";
  if (node.kind === "group") return `\\left(${expressionLatex(node.inner)}\\right)`;
  return `${expressionLatex(node.left)} ${symbol[node.op]} ${expressionLatex(node.right)}`;
}

function collectOperations(node) {
  if (node.kind === "value") return [];
  if (node.kind === "group") return collectOperations(node.inner);
  if (node.kind === "square") return [...collectOperations(node.inner), node];
  return [...collectOperations(node.left), ...collectOperations(node.right), node];
}

function operationStep(node) {
  if (node.kind === "square") {
    const inner = evaluate(node.inner);
    const result = evaluate(node);
    return { rule: "Najpierw obliczamy nawias, potem podnosimy licznik i mianownik do kwadratu.", latex: "\\left(" + fractionLatex(inner) + "\\right)^2 = \\frac{(" + inner.num + ")^2}{" + inner.den + "^2} = " + fractionLatex(result) };
  }
  const left = evaluate(node.left);
  const right = evaluate(node.right);
  const result = evaluate(node);
  if (node.op === "+" || node.op === "-") {
    const common = left.den * right.den / gcd(left.den, right.den);
    const expandedLeft = left.num * common / left.den;
    const expandedRight = right.num * common / right.den;
    const numerator = node.op === "+" ? expandedLeft + expandedRight : expandedLeft - expandedRight;
    return {
      rule: "Sprowadzamy ułamki do wspólnego mianownika, a potem liczymy liczniki.",
      latex: `${fractionLatex(left)} ${symbol[node.op]} ${fractionLatex(right)} = \\frac{${expandedLeft} ${symbol[node.op]} ${expandedRight}}{${common}} = \\frac{${numerator}}{${common}} = ${fractionLatex(result)}`,
    };
  }
  if (node.op === "*") {
    return {
      rule: "Mnożymy licznik przez licznik i mianownik przez mianownik, po czym skracamy wynik.",
      latex: `${fractionLatex(left)} \\cdot ${fractionLatex(right)} = \\frac{${left.num}\\cdot ${right.num}}{${left.den}\\cdot ${right.den}} = ${fractionLatex(result)}`,
    };
  }
  const reciprocal = fraction(right.den, right.num);
  return {
    rule: "Dzielenie zamieniamy na mnożenie przez odwrotność drugiego ułamka.",
    latex: `${fractionLatex(left)} : ${fractionLatex(right)} = ${fractionLatex(left)} \\cdot ${fractionLatex(reciprocal)} = ${fractionLatex(result)}`,
  };
}

export function generateTasks() {
  return TYPES.map((type) => {
    for (let attempt = 0; attempt < 500; attempt += 1) {
      const expression = makeExpression(type);
      const result = evaluate(expression);
      const manageableSteps = collectOperations(expression).every((node) => {
        if (evaluate(node).den > MAX_WORKING_DENOMINATOR) return false;
        if (node.op !== "+" && node.op !== "-") return true;
        const left = evaluate(node.left), right = evaluate(node.right);
        const common = left.den * right.den / gcd(left.den, right.den);
        return left.den !== right.den && common <= MAX_WORKING_DENOMINATOR;
      });
      if (manageableSteps && result.num !== 0 && Math.abs(result.num) <= result.den * 8 && result.den <= MAX_WORKING_DENOMINATOR) {
        return { type, data: { expression, result } };
      }
    }
    throw new Error(`Could not generate a suitable ${type}`);
  });
}

export function renderTask({ data }) {
  return <div><p>Oblicz. Wynik zapisz jako ułamek lub liczbę mieszaną.</p><BlockMath math={expressionLatex(data.expression)} /></div>;
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return <FractionInput previewFormatter={exactAnswerPreview} value={userAnswer} onChange={onChange} disabled={disabled} label="wynik" placeholder="np. 5/6 lub 1 1/2" />;
}

export function checkAnswer({ data }, userAnswer) {
  return checkExactMathAnswer(userAnswer, data.result);
}
export function renderAnswer({ data }) {
  return <InlineMath math={mixedLatex(data.result)} />;
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw;
  return raw ? (exactAnswerPreview(raw) ? <InlineMath math={exactAnswerPreview(raw)} /> : <span>{raw}</span>) : <i>(brak odpowiedzi)</i>;
}

export function renderExplanation({ data }) {
  const operations = collectOperations(data.expression);
  const mixedValues = [];
  const decimalValues = [];
  const visit = (node) => {
    if (node.kind === "value") {
      if (node.mixed) mixedValues.push(node.number);
      if (node.decimal) decimalValues.push(node);
      return;
    }
    if (node.kind === "group" || node.kind === "square") return visit(node.inner);
    visit(node.left);
    visit(node.right);
  };
  visit(data.expression);

  return (
    <div>
      <p>Najpierw zamieniamy liczby dziesiętne i mieszane na ułamki zwykłe. Liczymy w nawiasach, potem potęgi, mnożenie i dzielenie, a na końcu dodawanie i odejmowanie.</p>
      {decimalValues.map((node, index) => (
        <div key={"decimal-" + index}>
          <p>Liczbę dziesiętną zamieniamy na ułamek zwykły:</p>
          <BlockMath math={node.decimal.replace(",", "{,}") + " = " + fractionLatex(node.number)} />
        </div>
      ))}
      {mixedValues.map((number, index) => (
        <div key={`mixed-${index}`}>
          <p>Krok {index + 1}. Liczbę mieszaną zamieniamy na ułamek niewłaściwy:</p>
          <BlockMath math={`${mixedLatex(number)} = ${fractionLatex(number)}`} />
        </div>
      ))}
      {operations.map((node, index) => {
        const step = operationStep(node);
        return (
          <div key={`operation-${index}`}>
            <p>Krok {mixedValues.length + index + 1}. {step.rule}</p>
            <BlockMath math={step.latex} />
          </div>
        );
      })}
      <p>Po wykonaniu działań otrzymujemy wynik:</p>
      <BlockMath math={`${expressionLatex(data.expression)} = ${fractionLatex(data.result)}${mixedLatex(data.result) !== fractionLatex(data.result) ? ` = ${mixedLatex(data.result)}` : ""}`} />
    </div>
  );
}

const adapter = { renderTask, renderInput, checkAnswer, renderAnswer, renderUserAnswer, renderExplanation };
export const registry = Object.fromEntries(TYPES.map((type) => [type, adapter]));
